import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Save, X, GripVertical, Eye, EyeOff, Pencil } from 'lucide-react';
import { API } from '../config';

const playfair = "'Playfair Display', serif";
const cardStyle = { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' };
const inputStyle = { background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', color: '#F8FAF9' };

export const CategoriesManager = () => {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', icon_url: '', visible: true });

  useEffect(() => { fetchCats(); }, []);

  const fetchCats = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/categories`, { withCredentials: true });
      setCats(data);
    } catch (e) {
      toast.error('Gagal memuat kategori');
    } finally { setLoading(false); }
  };

  const startEdit = (c) => {
    setEditId(c.id);
    setForm({ name: c.name, icon_url: c.icon_url || '', visible: !!c.visible });
    setShowForm(true);
  };
  const startCreate = () => {
    setEditId(null);
    setForm({ name: '', icon_url: '', visible: true });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Nama kategori wajib'); return; }
    try {
      if (editId) {
        await axios.put(`${API}/admin/categories/${editId}`, form, { withCredentials: true });
        toast.success('Kategori diperbarui');
      } else {
        await axios.post(`${API}/admin/categories`, form, { withCredentials: true });
        toast.success('Kategori ditambah');
      }
      setShowForm(false); setEditId(null); setForm({ name: '', icon_url: '', visible: true });
      fetchCats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menyimpan');
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Hapus kategori "${c.name}"? Produk akan dipindah ke "Lainnya".`)) return;
    try {
      await axios.delete(`${API}/admin/categories/${c.id}`, { withCredentials: true });
      toast.success('Kategori dihapus');
      fetchCats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menghapus');
    }
  };

  const toggleVisibility = async (c) => {
    try {
      await axios.put(`${API}/admin/categories/${c.id}`, { ...c, visible: !c.visible }, { withCredentials: true });
      fetchCats();
    } catch (e) {
      toast.error('Gagal mengubah visibility');
    }
  };

  const moveCat = async (idx, dir) => {
    const newOrder = [...cats];
    const tgt = idx + dir;
    if (tgt < 0 || tgt >= newOrder.length) return;
    [newOrder[idx], newOrder[tgt]] = [newOrder[tgt], newOrder[idx]];
    setCats(newOrder); // optimistic
    try {
      await axios.post(`${API}/admin/categories/reorder`, { order: newOrder.map((c) => c.id) }, { withCredentials: true });
    } catch (e) {
      toast.error('Gagal memindah urutan');
      fetchCats();
    }
  };

  return (
    <div className="space-y-5" data-testid="categories-manager">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-light" style={{ fontFamily: playfair, color: '#F8FAF9' }}>Kategori Produk</h2>
          <p className="text-xs mt-1" style={{ color: '#A3B8AC' }}>Tambah / sunting / atur urutan kategori brand. Customer melihat sesuai urutan ini di homepage.</p>
        </div>
        <button onClick={startCreate} className="flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold" style={{ background: '#D4AF37', color: '#080C0A' }} data-testid="add-category-btn">
          <Plus className="w-4 h-4" /> Tambah Kategori
        </button>
      </div>

      {showForm && (
        <div className="rounded-2xl p-5 space-y-3 backdrop-blur-xl" style={cardStyle} data-testid="category-form">
          <div className="flex items-center justify-between">
            <h3 className="text-sm uppercase tracking-wider" style={{ color: '#D4AF37' }}>{editId ? 'Edit Kategori' : 'Kategori Baru'}</h3>
            <button onClick={() => { setShowForm(false); setEditId(null); }} style={{ color: '#A3B8AC' }}><X className="w-4 h-4" /></button>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nama kategori (mis. Nike)" className="rounded-lg px-3 py-2.5 outline-none" style={inputStyle} data-testid="category-name-input" />
            <input value={form.icon_url} onChange={(e) => setForm({ ...form, icon_url: e.target.value })} placeholder="Icon URL (opsional)" className="rounded-lg px-3 py-2.5 outline-none" style={inputStyle} data-testid="category-icon-input" />
          </div>
          <label className="flex items-center gap-2 text-sm" style={{ color: '#A3B8AC' }}>
            <input type="checkbox" checked={form.visible} onChange={(e) => setForm({ ...form, visible: e.target.checked })} />
            Tampilkan di homepage
          </label>
          <button onClick={save} className="px-5 py-2 rounded-full font-semibold flex items-center gap-2" style={{ background: '#D4AF37', color: '#080C0A' }} data-testid="save-category-btn">
            <Save className="w-4 h-4" /> {editId ? 'Simpan Perubahan' : 'Tambah'}
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12" style={{ color: '#A3B8AC' }}>Loading...</div>
      ) : (
        <div className="space-y-2" data-testid="categories-list">
          {cats.map((c, idx) => (
            <div key={c.id} className="rounded-xl p-3.5 flex items-center gap-3" style={cardStyle} data-testid={`category-row-${c.id}`}>
              <span className="text-xs w-7 text-center" style={{ color: '#A3B8AC' }}>{c.display_order}</span>
              <div className="flex flex-col">
                <button onClick={() => moveCat(idx, -1)} disabled={idx === 0} className="disabled:opacity-30" style={{ color: '#A3B8AC' }} data-testid={`move-up-${c.id}`}>▲</button>
                <button onClick={() => moveCat(idx, 1)} disabled={idx === cats.length - 1} className="disabled:opacity-30" style={{ color: '#A3B8AC' }} data-testid={`move-down-${c.id}`}>▼</button>
              </div>
              {c.icon_url ? (
                <img src={c.icon_url} alt={c.name} className="w-9 h-9 rounded-lg object-cover" />
              ) : (
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(212,175,55,0.15)' }}>
                  <GripVertical className="w-4 h-4" style={{ color: '#D4AF37' }} />
                </div>
              )}
              <div className="flex-1">
                <p className="font-medium" style={{ color: '#F8FAF9' }}>{c.name}</p>
                <p className="text-xs" style={{ color: c.visible ? '#22C55E' : '#A3B8AC' }}>{c.visible ? 'Tampil di homepage' : 'Tersembunyi'}</p>
              </div>
              <button onClick={() => toggleVisibility(c)} className="p-2" data-testid={`toggle-vis-${c.id}`}>
                {c.visible ? <Eye className="w-4 h-4" style={{ color: '#22C55E' }} /> : <EyeOff className="w-4 h-4" style={{ color: '#A3B8AC' }} />}
              </button>
              <button onClick={() => startEdit(c)} className="p-2" data-testid={`edit-cat-${c.id}`}>
                <Pencil className="w-4 h-4" style={{ color: '#D4AF37' }} />
              </button>
              <button onClick={() => remove(c)} className="p-2" data-testid={`delete-cat-${c.id}`}>
                <Trash2 className="w-4 h-4" style={{ color: '#EF4444' }} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
